import React, { useState } from "react";
import { TextField, MenuItem, Button } from "@mui/material";
import MainCalculatorsList from "../MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds,   FirstAds , MobileUnderCalAds } from "../../Ads";
import { CalculatorsType } from "../ConstantsEN";
import SharedHelmet from "../Helmet";
import * as yup from "yup";
import { Field, Form, Formik } from "formik";
const TITLE = "Engine Horsepower Calculator - Calculate Car Engine Power";
const DESCRIPTION = "Use the Engine Horsepower Calculator to estimate the power of your vehicle's engine. Enter key details and get accurate results.";
const KEYWORDS = "Engine Horsepower Calculator, Horsepower Calculation, Vehicle Power Estimation, Engine Power, Car Performance";
const OG_URL = 'https://do-calculate.com/calculator/en/horsepower';
const weightConversionMap = {
  pound: 1,
  ton: 2204.6226218,
  kg: 2.2046226218,
};
const timeConversionMap = {
  second: 1,
  minute: 60,
  hour: 3600,
  day: 3600 * 24,
};
const speedConversionMap = {
  "miles per hour": 1,
  "kilometers per hour": 0.6213711922,
  "meters per second": 2.236936,
};
const ETSchema = yup.object().shape({
  vehicleWeight: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  ET: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialETValues = {
  vehicleWeight: "",
  ET: "",
  vehicleWeightUnit: "pound",
  ETUnit: "second",
};
const TSSchema = yup.object().shape({
  vehicleWeight: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  TS: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialTSValues = {
  vehicleWeight: "",
  TS: "",
  vehicleWeightUnit: "pound",
  TSUnit: "miles per hour",
};
function EngineHorsepower() {
  const [horsepowerET, setHorsepowerET] = useState("");
  const onSubmitET = (values) => {
    const { vehicleWeight, ET, vehicleWeightUnit, ETUnit } = values;
    const ConversionWeight =
      vehicleWeight * weightConversionMap[vehicleWeightUnit];
    const ConversionET = ET * timeConversionMap[ETUnit];
    const factor = Math.pow(ConversionET / 5.825, 3);
    const horsepowerET = ConversionWeight / factor;
    setHorsepowerET(horsepowerET.toFixed(2));
  };
  const [horsepowerTS, setHorsepowerTS] = useState("");
  const onSubmitTS = (values) => {
    const { vehicleWeight, TS, vehicleWeightUnit, TSUnit } = values;
    const ConversionWeight =
      vehicleWeight * weightConversionMap[vehicleWeightUnit];
    const ConversionTS = TS * speedConversionMap[TSUnit];
    const factor = Math.pow(ConversionTS / 234, 3);
    const horsepowerTS = ConversionWeight * factor;
    setHorsepowerTS(horsepowerTS.toFixed(3));
  };
  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "32px" }}
          >
            <h1 style={{ fontSize: "14px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Welcome to the Engine Horsepower Calculator, the efficient tool that helps you calculate and estimate the power of your car's engine with ease.
                  Whether you care about the performance of your current car or are considering buying a new one, this calculator can provide you with accurate statistics
                  about the horsepower of the engine.
                </p>
                <h2 style={{ fontSize: '17px' }}>How to Use the Calculator:</h2>
                <p>
                  Using our calculator is very simple. Enter key information about your car's engine, such as the number of cylinders, displacement size, and torque.
                  Once you input this data, the calculator will automatically calculate the engine power in horsepower.
                </p>
                <h2 style={{ fontSize: '17px' }}>Benefits of the Engine Horsepower Calculator:</h2>
                <ul>
                  <li>Provide accurate estimation of engine performance.</li>
                  <li>Better understanding of the car's power and efficiency.</li>
                  <li>Assistance in making better decisions when buying a new car.</li>
                </ul>
                <p>
                  Get accurate and useful information about your car's engine power now. Visit the Engine Horsepower Calculator and calculate performance easily and conveniently!
                </p>
                <p>
                  The following two calculators can be used to estimate the horsepower of the car's engine based on the vehicle weight, elapsed time, and speed used to finish a quarter-mile distance.
                  The weight of the vehicle should include not only the car but also the driver, passenger, and any other significant weight. To estimate the maximum horsepower output,
                  the maximum output from start to finish must be applied. All calculation results are estimates.
                  <br />
                  <b>Elapsed Time (ET) Method:</b> This method uses the weight of the car and the elapsed time (ET) to finish a quarter-mile (402.3 meters) distance according to the following formula: <br />
                  Weight / (ET/5.825)^3 <br />
                  <b>Trap Speed Method:</b> This method uses the weight of the vehicle and the speed at which the vehicle covered a quarter-mile (402.3 meters) distance according to the following formula: <br />
                  Weight * (Speed / 234)^3 <br />
                  The speed used should be the speed reached at the quarter-mile point, not the average speed.
                </p>
                <hr />
              </div>
              <br />
            </div>
             <MobileUnderCalAds/>
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <Formik
                  initialValues={initialETValues}
                  validationSchema={ETSchema}
                  onSubmit={onSubmitET}
                >
                  {(formik) => (
                    <Form>
                      <div className="mb-3">
                        <h3
                          className="p-3 mb-2 bg-success text-white"
                          style={{ fontSize: "16px", fontWeight: "bold" }}
                        >
                          The Elapsed Time (ET) Method
                        </h3>
                        <TextField
                          label="Car Weight"
                          name="vehicleWeight"
                          value={formik.values.vehicleWeight}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.vehicleWeight &&
                            formik.errors.vehicleWeight
                          }
                          helperText={
                            formik.touched.vehicleWeight &&
                            formik.errors.vehicleWeight
                          }
                          sx={{
                            width: "160px",
                            ml: "10px",
                            bgcolor: "#f9f9f9",
                            "&  .MuiFormHelperText-root.Mui-error": {
                              backgroundColor: "rgb(229, 230, 227) !important",
                              margin: 0,
                              padding: "8px 0 0 41px",
                            },
                          }}
                          size="small"
                          color="success"
                        />
                        <Field
                          as={TextField}
                          select
                          name="vehicleWeightUnit"
                          value={formik.values.vehicleWeightUnit}
                          onChange={formik.handleChange}
                          size="small"
                          style={{
                            backgroundColor: "#f9f9f9",
                            pending: "5px",
                          }}
                        >
                          {Object.keys(weightConversionMap).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Field>
                      </div>
                      <div className="mb-3">
                        <TextField
                          label="Time to Complete Quarter Mile"
                          name="ET"
                          value={formik.values.ET}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.ET && formik.errors.ET}
                          helperText={formik.touched.ET && formik.errors.ET}
                          sx={{
                            width: "160px",
                            ml: "10px",
                            bgcolor: "#f9f9f9",
                            "&  .MuiFormHelperText-root.Mui-error": {
                              backgroundColor: "rgb(229, 230, 227) !important",
                              margin: 0,
                              padding: "8px 0 0 41px",
                            },
                          }}
                          size="small"
                          color="success"
                        />
                        <Field
                          as={TextField}
                          select
                          name="ETUnit"
                          value={formik.values.ETUnit}
                          onChange={formik.handleChange}
                          size="small"
                          style={{
                            backgroundColor: "#f9f9f9",
                            pending: "5px",
                          }}
                        >
                          {Object.keys(timeConversionMap).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Field>
                      </div>
                      <div className="mb-3">
                        <Button
                          type="submit"
                          disabled={!formik.isValid}
                          color="success"
                          sx={{
                            backgroundColor: "rgb(25,135,84)",
                            fontSize: "15px",
                            marginRight: "25px",
                            padding: "5px 25px",
                            color: "white",
                          }}
                        >
                          Calculate
                        </Button>
                        <Button
                          disabled={!formik.isValid}
                          sx={{
                            backgroundColor: "rgb(108,117,125)",
                            fontSize: "15px",
                            marginRight: "25px",
                            padding: "5px 25px",
                            color: "white",
                          }}
                          onClick={() => {
                            formik.resetForm();
                            setHorsepowerET("");
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                      {horsepowerET &&
                        horsepowerET !== "NaN" &&
                        horsepowerET !== "Infinity" && (
                          <div
                            style={{ fontSize: "15px" }}
                            className=" bg-light  text-dark"
                          >
                            <div className="p-3 bg-success text-white mb-2">
                              <strong>
                                Result: Estimated Engine Power: {horsepowerET} Horsepower
                              </strong>
                            </div>
                          </div>
                        )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <Formik
                  initialValues={initialTSValues}
                  validationSchema={TSSchema}
                  onSubmit={onSubmitTS}
                >
                  {(formik) => (
                    <Form>
                      <div className="mb-3">
                        <h3
                          className="p-3 mb-2 bg-success text-white"
                          style={{ fontSize: "16px", fontWeight: "bold" }}
                        >
                          The Trap-Speed Method
                        </h3>
                        <TextField
                          label="Car Weight"
                          name="vehicleWeight"
                          value={formik.values.vehicleWeight}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.vehicleWeight &&
                            formik.errors.vehicleWeight
                          }
                          helperText={
                            formik.touched.vehicleWeight &&
                            formik.errors.vehicleWeight
                          }
                          sx={{
                            width: "160px",
                            ml: "10px",
                            bgcolor: "#f9f9f9",
                            "&  .MuiFormHelperText-root.Mui-error": {
                              backgroundColor: "rgb(229, 230, 227) !important",
                              margin: 0,
                              padding: "8px 0 0 41px",
                            },
                          }}
                          size="small"
                          color="success"
                        />
                        <Field
                          as={TextField}
                          select
                          name="vehicleWeightUnit"
                          value={formik.values.vehicleWeightUnit}
                          onChange={formik.handleChange}
                          size="small"
                          style={{
                            backgroundColor: "#f9f9f9",
                            pending: "5px",
                          }}
                        >
                          {Object.keys(weightConversionMap).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Field>
                      </div>
                      <div className="mb-3">
                        <TextField
                          label="Speed at the End of Quarter Mile"
                          name="TS"
                          value={formik.values.TS}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.TS && formik.errors.TS}
                          helperText={formik.touched.TS && formik.errors.TS}
                          sx={{
                            width: "160px",
                            ml: "10px",
                            bgcolor: "#f9f9f9",
                            "&  .MuiFormHelperText-root.Mui-error": {
                              backgroundColor: "rgb(229, 230, 227) !important",
                              margin: 0,
                              padding: "8px 0 0 41px",
                            },
                          }}
                          size="small"
                          color="success"
                        />
                        <Field
                          as={TextField}
                          select
                          name="TSUnit"
                          value={formik.values.TSUnit}
                          onChange={formik.handleChange}
                          size="small"
                          style={{
                            backgroundColor: "#f9f9f9",
                            pending: "5px",
                          }}
                        >
                          {Object.keys(speedConversionMap).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Field>
                      </div>
                      <div className="mb-3">
                        <Button
                          type="submit"
                          disabled={!formik.isValid}
                          color="success"
                          sx={{
                            backgroundColor: "rgb(25,135,84)",
                            fontSize: "15px",
                            marginRight: "25px",
                            padding: "5px 25px",
                            color: "white",
                          }}
                        >
                          Calculate
                        </Button>
                        <Button
                          disabled={!formik.isValid}
                          sx={{
                            backgroundColor: "rgb(108,117,125)",
                            fontSize: "15px",
                            marginRight: "25px",
                            padding: "5px 25px",
                            color: "white",
                          }}
                          onClick={() => {
                            formik.resetForm();
                            setHorsepowerTS("");
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                      {horsepowerTS &&
                        horsepowerTS !== "NaN" &&
                        horsepowerTS !== "Infinity" && (
                          <div
                            style={{ fontSize: "15px" }}
                            className=" bg-light  text-dark"
                          >
                            <div className="p-3 bg-success text-white mb-2">
                              <strong>
                                Result: Estimated Engine Power: {horsepowerTS} Horsepower
                              </strong>
                            </div>
                          </div>
                        )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
            <hr />
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList
            CalculatorsTypeId={CalculatorsType.physicsCalculators}
          ></MainCalculatorsList>
          <br />
        </div>
         </div>
</div>
  );
}
export default EngineHorsepower;
